import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Download, Send, Eye, Trash2, AlertCircle, CreditCard } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';

export default function CreditNotaPage() {
  const { api, user } = useAuth();
  const { t, i18n } = useTranslation();
  
  const [creditNotes, setCreditNotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedCreditNote, setSelectedCreditNote] = useState(null);
  const [reason, setReason] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [creditNotesRes, invoicesRes] = await Promise.all([
        api.get('/credit-notes'),
        api.get('/invoices')
      ]);
      setCreditNotes(creditNotesRes.data);
      // Only show invoices that can be credited (not already gecrediteerd)
      setInvoices(invoicesRes.data.filter(inv => inv.status !== 'gecrediteerd'));
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setReason('');
    setShowCreateDialog(true);
  };

  const createCreditNote = async () => {
    if (!reason.trim()) {
      toast.error(i18n.language === 'en' ? 'Please provide a reason for the credit note' : 'Vul een reden in voor de creditnota');
      return;
    }

    setCreating(true);
    try {
      await api.post('/credit-notes', {
        invoice_id: selectedInvoice.invoice_id,
        reden: reason.trim()
      });
      toast.success(i18n.language === 'en' ? 'Credit note created! Entries released for re-invoicing.' : 'Creditnota aangemaakt! Registraties vrijgegeven voor nieuwe facturatie.');
      setShowCreateDialog(false);
      setSelectedInvoice(null);
      setReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setCreating(false);
    }
  };

  const viewCreditNote = async (creditNote) => {
    try {
      const res = await api.get(`/credit-notes/${creditNote.credit_note_id}`);
      setSelectedCreditNote(res.data);
      setShowViewDialog(true);
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  const downloadPdf = async (creditNote) => {
    try {
      toast.info(t('common.loading'));
      const response = await api.get(`/credit-notes/${creditNote.credit_note_id}/pdf`, {
        responseType: 'blob'
      });
      
      const prefix = i18n.language === 'en' ? 'CreditNote' : 'Creditnota';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${prefix}_${creditNote.credit_note_nummer.replace(/-/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('common.download') + '!');
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  const sendCreditNote = async (creditNote) => {
    try {
      const res = await api.post(`/credit-notes/${creditNote.credit_note_id}/send`);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
    }
  };

  const updateStatus = async (creditNoteId, status) => {
    try {
      await api.put(`/credit-notes/${creditNoteId}/status?status=${status}`);
      toast.success(t('settings.saved'));
      fetchData();
      if (selectedCreditNote?.credit_note_id === creditNoteId) {
        setSelectedCreditNote(prev => ({ ...prev, status }));
      }
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  const deleteCreditNote = async (creditNote) => {
    const confirmMsg = i18n.language === 'en' 
      ? 'Are you sure you want to delete this credit note? The original invoice will be restored.'
      : 'Weet je zeker dat je deze creditnota wilt verwijderen? De originele factuur wordt hersteld.';
    
    if (!confirm(confirmMsg)) return;

    try {
      await api.delete(`/credit-notes/${creditNote.credit_note_id}`);
      toast.success(i18n.language === 'en' ? 'Credit note deleted' : 'Creditnota verwijderd');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      concept: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
      verzonden: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      verwerkt: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
    };
    const labels = {
      concept: i18n.language === 'en' ? 'Draft' : 'Concept',
      verzonden: i18n.language === 'en' ? 'Sent' : 'Verzonden',
      verwerkt: i18n.language === 'en' ? 'Processed' : 'Verwerkt'
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  const reasonOptions = i18n.language === 'en' 
    ? ['Invoice error', 'Return/Cancellation', 'Discount', 'Other']
    : ['Fout in factuur', 'Retour/Annulering', 'Korting', 'Anders'];

  if (loading) {
    return <div className="animate-pulse space-y-4">
      <div className="h-12 bg-muted rounded-xl w-1/3" />
      <div className="h-64 bg-muted rounded-xl" />
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="creditnota-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-heading text-foreground">
            {i18n.language === 'en' ? 'Credit Notes' : 'Creditnota\'s'}
          </h1>
          <p className="text-muted-foreground">
            {i18n.language === 'en' ? 'Manage credit notes and corrections' : 'Beheer creditnota\'s en correcties'}
          </p>
        </div>
      </div>

      {/* Facturen beschikbaar voor crediteren */}
      {invoices.length > 0 && (
        <Card className="border-0 shadow-sm">
          <div className="p-4 border-b bg-slate-50 dark:bg-slate-800/50">
            <h2 className="font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {i18n.language === 'en' ? 'Available for Credit' : 'Beschikbaar voor Crediteren'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {i18n.language === 'en' 
                ? 'Select an invoice to create a credit note. Entries will be released for re-invoicing.'
                : 'Selecteer een factuur om te crediteren. Registraties worden vrijgegeven voor nieuwe facturatie.'}
            </p>
          </div>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{i18n.language === 'en' ? 'Invoice #' : 'Factuurnr.'}</TableHead>
                    <TableHead>{t('common.client')}</TableHead>
                    <TableHead>{t('common.date')}</TableHead>
                    <TableHead className="text-right">{t('common.amount')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.invoice_id}>
                      <TableCell className="font-mono font-medium">{invoice.invoice_nummer}</TableCell>
                      <TableCell>{invoice.client_naam}</TableCell>
                      <TableCell>{invoice.datum}</TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(invoice.totaal)}</TableCell>
                      <TableCell>
                        <Badge className={
                          invoice.status === 'betaald' 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                            : invoice.status === 'verzonden'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                            : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                        }>
                          {invoice.status === 'betaald' ? (i18n.language === 'en' ? 'Paid' : 'Betaald') :
                           invoice.status === 'verzonden' ? (i18n.language === 'en' ? 'Sent' : 'Verzonden') :
                           (i18n.language === 'en' ? 'Draft' : 'Concept')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => openCreateDialog(invoice)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          data-testid={`credit-invoice-${invoice.invoice_id}`}
                        >
                          <CreditCard className="w-4 h-4 mr-1" />
                          {i18n.language === 'en' ? 'Credit' : 'Crediteer'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Creditnota's lijst */}
      <Card className="border-0 shadow-sm">
        <div className="p-4 border-b bg-red-50 dark:bg-red-900/20">
          <h2 className="font-semibold flex items-center gap-2 text-red-700 dark:text-red-400">
            <CreditCard className="w-4 h-4" />
            {i18n.language === 'en' ? 'Credit Notes' : 'Creditnota\'s'}
            {creditNotes.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground">({creditNotes.length})</span>
            )}
          </h2>
        </div>
        <CardContent className="p-0">
          {creditNotes.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{i18n.language === 'en' ? 'Credit Note #' : 'Creditnota nr.'}</TableHead>
                    <TableHead>{i18n.language === 'en' ? 'Ref. Invoice' : 'Ref. Factuur'}</TableHead>
                    <TableHead>{t('common.client')}</TableHead>
                    <TableHead>{t('common.date')}</TableHead>
                    <TableHead>{i18n.language === 'en' ? 'Reason' : 'Reden'}</TableHead>
                    <TableHead className="text-right">{t('common.amount')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead className="w-32"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {creditNotes.map((cn) => (
                    <TableRow key={cn.credit_note_id}>
                      <TableCell className="font-mono font-medium text-red-600">{cn.credit_note_nummer}</TableCell>
                      <TableCell className="font-mono text-sm">{cn.invoice_nummer}</TableCell>
                      <TableCell>{cn.client_naam}</TableCell>
                      <TableCell>{cn.datum}</TableCell>
                      <TableCell className="max-w-[150px] truncate" title={cn.reden}>{cn.reden}</TableCell>
                      <TableCell className="text-right font-semibold text-red-600">{formatCurrency(cn.totaal)}</TableCell>
                      <TableCell>{getStatusBadge(cn.status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => viewCreditNote(cn)}
                            title={t('common.edit')}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => downloadPdf(cn)}
                            title={i18n.language === 'en' ? 'Download PDF' : 'Download PDF'}
                            className="text-green-600"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {cn.status === 'concept' && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => sendCreditNote(cn)}
                              className="text-blue-600"
                              title={i18n.language === 'en' ? 'Send via email' : 'Verstuur per email'}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          {cn.status === 'concept' && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => deleteCreditNote(cn)}
                              className="text-destructive hover:text-destructive"
                              title={i18n.language === 'en' ? 'Delete' : 'Verwijderen'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {i18n.language === 'en' 
                  ? 'No credit notes yet. Select an invoice above to create one.'
                  : 'Nog geen creditnota\'s. Selecteer een factuur hierboven om er een aan te maken.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Credit Note Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <CreditCard className="w-5 h-5" />
              {i18n.language === 'en' ? 'Create Credit Note' : 'Creditnota Aanmaken'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800 dark:text-red-300">
                      {i18n.language === 'en' 
                        ? 'This will credit the entire invoice'
                        : 'Dit crediteert de gehele factuur'}
                    </p>
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                      {i18n.language === 'en' 
                        ? 'All linked entries will be released and can be invoiced again.'
                        : 'Alle gekoppelde registraties worden vrijgegeven en kunnen opnieuw gefactureerd worden.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{i18n.language === 'en' ? 'Invoice:' : 'Factuur:'}</span>
                  <span className="font-mono font-medium">{selectedInvoice.invoice_nummer}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('common.client')}:</span>
                  <span>{selectedInvoice.client_naam}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{i18n.language === 'en' ? 'Amount:' : 'Bedrag:'}</span>
                  <span className="font-semibold">{formatCurrency(selectedInvoice.totaal)}</span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between text-red-600 font-medium">
                  <span>{i18n.language === 'en' ? 'Credit amount:' : 'Creditbedrag:'}</span>
                  <span>{formatCurrency(-selectedInvoice.totaal)}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{i18n.language === 'en' ? 'Reason for credit note *' : 'Reden voor creditnota *'}</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {reasonOptions.map((opt) => (
                    <Button
                      key={opt}
                      type="button"
                      variant={reason === opt ? "default" : "outline"}
                      size="sm"
                      onClick={() => setReason(opt)}
                      className={reason === opt ? "bg-red-600 hover:bg-red-700" : ""}
                    >
                      {opt}
                    </Button>
                  ))}
                </div>
                <Textarea
                  placeholder={i18n.language === 'en' ? 'Enter reason or add details...' : 'Vul reden in of voeg details toe...'}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={createCreditNote} 
              disabled={creating || !reason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {creating ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {i18n.language === 'en' ? 'Creating...' : 'Aanmaken...'}
                </span>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  {i18n.language === 'en' ? 'Create Credit Note' : 'Creditnota Aanmaken'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Credit Note Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-red-600" />
              {selectedCreditNote?.credit_note_nummer}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCreditNote && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">{i18n.language === 'en' ? 'Ref. Invoice:' : 'Ref. Factuur:'}</span>
                  <span className="ml-2 font-mono">{selectedCreditNote.invoice_nummer}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('common.client')}:</span>
                  <span className="ml-2">{selectedCreditNote.client_naam}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('common.date')}:</span>
                  <span className="ml-2">{selectedCreditNote.datum}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('common.status')}:</span>
                  <span className="ml-2">{getStatusBadge(selectedCreditNote.status)}</span>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
                <span className="text-muted-foreground text-sm">{i18n.language === 'en' ? 'Reason:' : 'Reden:'}</span>
                <p className="mt-1">{selectedCreditNote.reden}</p>
              </div>

              {/* Line items */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{i18n.language === 'en' ? 'Description' : 'Omschrijving'}</TableHead>
                    <TableHead className="text-right">{t('common.amount')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedCreditNote.regels?.map((regel, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{regel.beschrijving}</TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(regel.bedrag)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span>{i18n.language === 'en' ? 'Subtotal' : 'Subtotaal'}</span>
                  <span className="text-red-600">{formatCurrency(selectedCreditNote.subtotaal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>BTW</span>
                  <span className="text-red-600">{formatCurrency(selectedCreditNote.btw_bedrag)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg">
                  <span>{t('common.total')}</span>
                  <span className="text-red-600">{formatCurrency(selectedCreditNote.totaal)}</span>
                </div>
              </div>

              {/* Status update buttons */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                {selectedCreditNote.status !== 'verzonden' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedCreditNote.credit_note_id, 'verzonden')}
                  >
                    {i18n.language === 'en' ? 'Mark as Sent' : 'Markeer als Verzonden'}
                  </Button>
                )}
                {selectedCreditNote.status !== 'verwerkt' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedCreditNote.credit_note_id, 'verwerkt')}
                    className="text-green-600"
                  >
                    {i18n.language === 'en' ? 'Mark as Processed' : 'Markeer als Verwerkt'}
                  </Button>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              {t('common.close')}
            </Button>
            <Button 
              onClick={() => downloadPdf(selectedCreditNote)}
              className="bg-green-600 hover:bg-green-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
